<script lang="ts">
  import { SearchState } from "./services/search-service";
  import SideSearch from "./components/SideSearch.svelte";
  import LocationSearchInput from "./components/search/LocationSearchInput.svelte";
  import WhenSearchInput from "./components/search/WhenSearchInput.svelte";
  import EverythingSearchInput from "./components/search/EverythingSearchInput.svelte";
  import type { PublicationsSearchResultsModel } from "./models/publications.interface";
  import ErrorComponent from "./components/ErrorComponent.svelte";
  import DropdownInput from "./components/search/DropdownInput.svelte";
  import PublicationsSearchResults from "./components/search/PublicationsSearchResults.svelte";
  import Pageination from "./components/Pageination.svelte";
  import { fileStore } from "./services/select-files";
  import { showCartAlert } from "./services/cart-service";
  import CartAlert from "./components/search/CartAlert.svelte";
  import CartNotification from "./components/search/CartNotification.svelte";
  import OrderOptionsModal from "./components/order/OrderOptionsModal.svelte";

  const publicationMetadata = JSON.parse(document.getElementById("publicationMetadata").innerHTML);
  const searchOptions: { [optionId: string]: any } = publicationMetadata.ncei.access.search;

  const PAGE_SIZE = 20;
  export let searchEndpoint = new URL(process.env.SEARCH_URL);
  let pubsLoaded: Promise<PublicationsSearchResultsModel>;

  SearchState.subscribe((search) => {
    search.params.set("limit", PAGE_SIZE.toString());
    search.params.set("offset", ((search.pageNum - 1) * PAGE_SIZE).toString());
    pubsLoaded = fetchPubs(search.params);
  });

  // When files are selected, show the CartAlert
  // $: showCartAlert.set(!!($fileStore.selectedFiles.length > 0));

  async function fetchPubs(params: URLSearchParams): Promise<PublicationsSearchResultsModel> {
    const response = await fetch(
      `${searchEndpoint}/v2/publications/${publicationMetadata.id}?${params}`
    );
    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const resp = await response.json();
        const message = resp.errors?.[0]?.message || resp.errorMessage || "Unknown error";
        const code = resp.errorCode || response.status;
        errorMessage = `HTTP error! status: ${code} message: ${message}`;
      } catch {
        errorMessage = `HTTP error! status: ${response.status} text: ${await response.text().catch(() => 'Unknown')}`;
      }

      let e = new Error(errorMessage);
      e.name = "HTTP_ERROR";
      throw e;
    }
    return await response.json();
  }
</script>

<div class="container-fluid">
  <div class="row">
    <div class="col-lg-2 gx-0" on:submit|preventDefault>
      <SideSearch>
        <svelte:component
          this={searchOptions.hasOwnProperty("title") ? EverythingSearchInput : null}
          slot="datatypes"
          {...searchOptions?.title?.params}
        />
        <svelte:component
          this={searchOptions.hasOwnProperty("where") ? LocationSearchInput : null}
          slot="where"
          tooltip="Select location from autocomplete."
          {...searchOptions?.where?.params}
        />
        <WhenSearchInput slot="when" enabled={true} format={"Date"} />
        <svelte:component
          this={searchOptions.hasOwnProperty("station") ? EverythingSearchInput : null}
          slot="stations"
          {...searchOptions?.station?.params}
        />
        <svelte:component
          this={searchOptions.hasOwnProperty("documentperiod") ? DropdownInput : null}
          slot="accessLevel"
          title="Document Period"
          tooltip="Choose document period"
          placeholder="Choose..."
          {...searchOptions?.documentperiod?.params}
        />
        <svelte:component
          this={searchOptions.hasOwnProperty("author") ? EverythingSearchInput : null}
          slot="weatherFlags"
          {...searchOptions?.author?.params}
        />
        <svelte:component
          this={searchOptions.hasOwnProperty("series") ? EverythingSearchInput : null}
          slot="channels"
          {...searchOptions?.series?.params}
        />
      </SideSearch>
    </div>
    <main class="col-md-10">
      {#await pubsLoaded}
        <div class="loading-container">
          <div
            class="spinner-border text-secondary"
            role="status"
            style="width: 3rem; height: 3rem;"
          >
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>
      {:then pubs}
        <div class="row dataset">
          <div class="col pt-3 ps-3">
            <h2 style="color: white;">{publicationMetadata?.name}</h2>
          </div>
        </div>
        <div class="row" style="background-color: #F1F3F6;">
          <div class="col mb-3">
          <div class="pt-3">
            <div>
              {publicationMetadata.ncei.description}
            </div>
          </div>
          </div>
        </div>

        <div class="d-flex flex-row align-items-center justify-content-evenly mt-2 pb-2">
          <div class="row">
            <div class="col">
                <div class="d-flex justify-content-center my-2">
                  <div
                    class="badge"
                    style="--bs-btn-border-radius: 200px; width: 20em; --bs-btn-font-weight: bold; background-color: #007EC6"
                  >
                    {pubs?.count || 0} publications found
                  </div>
                </div>
            </div>
            <div class="row">
              <div class="col">
              <!-- Use the new child component to render results -->
              <PublicationsSearchResults {pubs} fields={publicationMetadata?.ncei?.access?.search?.publicationfields || []} />

              <!-- Pagination -->
              {#if pubs?.count}
                <div class="row">
                  <div class="col">
                    <Pageination pageSize={PAGE_SIZE} count={pubs?.count} />
                  </div>
                </div>
              {/if}
            </div>
            </div>
          </div>
        </div>
      {:catch error}
        <ErrorComponent {error} />
      {/await}

      <CartNotification />

      {#if $fileStore.selectedFiles.length > 0 && $showCartAlert}
        <CartAlert datasetMetadata={publicationMetadata} />
      {/if}

      <OrderOptionsModal
        selectedFileList={$fileStore.selectedFiles}
        datasetMetadata={publicationMetadata}
      />
    </main>
  </div>
</div>

<style>
  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
    width: 100%;
  }
</style>
